// Client-side video bookmarks. The full card payload is stored at bookmark
// time (on the video page, where the data is available) so the bookmarks menu
// can render offline from localStorage on any page, with no server round-trip.

import { buildCard } from './card';
import { esc } from './utils';

export interface Bookmark {
  id: string; // Vimeo id — matches `.card[data-video-id]` and the player.
  title: string;
  state: string;
  color: string;
  url: string;
  thumbnail: string;
  /** Width-descriptor srcset for the thumbnail; may be absent on older saves. */
  srcset?: string;
  /** `sizes` attribute paired with `srcset`. */
  sizes?: string;
  type: 'theme' | 'perspective';
  duration: string;
}

const STORAGE_KEY = 'states:bookmarks';

type Listener = (bookmarks: Bookmark[]) => void;
const listeners = new Set<Listener>();

/** Reads bookmarks newest-first, tolerating missing/corrupt storage. */
export function readBookmarks(): Bookmark[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Bookmark[]) : [];
  } catch {
    return [];
  }
}

function writeBookmarks(bookmarks: Bookmark[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(bookmarks));
  } catch {
    /* private mode / quota — the feature is non-essential, so ignore. */
  }
  listeners.forEach((fn) => fn(bookmarks));
}

export function isBookmarked(id: string): boolean {
  return readBookmarks().some((b) => b.id === id);
}

export function getBookmarkCount(): number {
  return readBookmarks().length;
}

/** Toggles a bookmark and returns the new state (true = now bookmarked). */
export function toggleBookmark(item: Bookmark): boolean {
  const current = readBookmarks();
  const exists = current.some((b) => b.id === item.id);
  // Newest entries first so the menu reads most-recent at the top.
  const next = exists ? current.filter((b) => b.id !== item.id) : [item, ...current];
  writeBookmarks(next);
  return !exists;
}

export function removeBookmark(id: string): void {
  const current = readBookmarks();
  if (current.some((b) => b.id === id)) {
    writeBookmarks(current.filter((b) => b.id !== id));
  }
}

/** Subscribes to bookmark changes; returns an unsubscribe fn. */
export function subscribeBookmarks(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── Video-page toggle button ─────────────────────────────────────────────────

/** Wires the bookmark toggle(s) on a video page (`.js-bookmark-toggle`). */
export function initBookmarkToggles(): void {
  const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('.js-bookmark-toggle'));
  if (buttons.length === 0) return;

  buttons.forEach((btn) => {
    const item = readToggleData(btn);
    if (!item) return;

    const sync = (): void => {
      const on = isBookmarked(item.id);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', on ? 'Remove bookmark' : 'Bookmark this video');
    };

    sync();
    subscribeBookmarks(sync); // keep in sync if changed from the menu/another tab path
    btn.addEventListener('click', () => {
      toggleBookmark(item);
      sync();

      // The pointer is still over the button after this click, but the
      // hover preview now points the wrong way (e.g. just bookmarked, but
      // "bookmarked + hover" previews removal) — suppress it until the
      // pointer actually leaves and re-enters. See `.js-suppress-hover` in
      // _video.scss.
      btn.classList.add('js-suppress-hover');
    });
    btn.addEventListener('pointerleave', () => {
      btn.classList.remove('js-suppress-hover');
    });
  });
}

function readToggleData(btn: HTMLElement): Bookmark | null {
  const id = btn.dataset.bookmarkId;
  if (!id) return null;
  const type = btn.dataset.bookmarkType === 'theme' ? 'theme' : 'perspective';
  return {
    id,
    title: btn.dataset.bookmarkTitle ?? '',
    state: btn.dataset.bookmarkState ?? '',
    color: btn.dataset.bookmarkColor ?? '',
    url: btn.dataset.bookmarkUrl ?? '',
    thumbnail: btn.dataset.bookmarkThumbnail ?? '',
    srcset: btn.dataset.bookmarkSrcset ?? '',
    sizes: btn.dataset.bookmarkSizes ?? '',
    type,
    duration: btn.dataset.bookmarkDuration ?? '',
  };
}

// ── Menu rendering ───────────────────────────────────────────────────────────

// Mirrors templates/partials/card.html.twig (+ a remove control).
function buildBookmarkCard(item: Bookmark): string {
  return buildCard({
    type: item.type,
    videoId: item.id,
    title: item.title,
    state: item.state,
    color: item.color,
    url: item.url,
    thumbnail: item.thumbnail,
    srcset: item.srcset,
    sizes: item.sizes,
    duration: item.duration,
  });
}

function buildBookmarkItem(item: Bookmark): HTMLElement {
  const template = document.createElement('template');
  template.innerHTML = `<li class="bookmarks__item" data-bookmark-id="${esc(item.id)}">
      ${buildBookmarkCard(item)}
      <button class="bookmarks__remove js-bookmark-remove" type="button" data-bookmark-id="${esc(item.id)}" aria-label="Remove ${esc(item.title)}">
        <span class="bookmarks__remove-text" aria-hidden="true">Remove</span>
        <svg class="bookmarks__remove-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false" width="14" height="14"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </button>
    </li>`;

  const el = template.content.firstElementChild as HTMLElement;

  // Remove control (delegation would also work, but a listener per row is cheap
  // and keeps the wiring next to the markup it belongs to).
  el.querySelector<HTMLButtonElement>('.js-bookmark-remove')?.addEventListener('click', (e) => {
    e.preventDefault();
    removeBookmark(item.id);
  });

  return el;
}

/**
 * Reconciles the menu list against the stored bookmarks, toggling the empty
 * state.
 *
 * Rows are matched by id and reused rather than re-rendered wholesale: removing
 * one bookmark must not restart the entrance stagger on the rows that stayed
 * put (the animation is keyed off `.is-open`, so a fresh element replays it —
 * see `bookmarks-card-in` in _bookmarks.scss), and reusing the node also spares
 * the thumbnail a re-request.
 */
export function renderBookmarksInto(listEl: HTMLElement, emptyEl: HTMLElement | null): void {
  const bookmarks = readBookmarks();
  if (emptyEl) emptyEl.hidden = bookmarks.length > 0;

  const existing = new Map<string, HTMLElement>();
  listEl.querySelectorAll<HTMLElement>('.bookmarks__item[data-bookmark-id]').forEach((el) => {
    existing.set(el.dataset.bookmarkId ?? '', el);
  });

  const wanted = new Set(bookmarks.map((b) => b.id));
  existing.forEach((el, id) => {
    if (!wanted.has(id)) el.remove();
  });

  // Walk the desired order alongside the surviving rows, touching the DOM only
  // where the two disagree.
  let cursor: Element | null = listEl.firstElementChild;
  bookmarks.forEach((item) => {
    const el = existing.get(item.id);
    if (el && el === cursor) {
      cursor = cursor.nextElementSibling;
      return;
    }
    listEl.insertBefore(el ?? buildBookmarkItem(item), cursor);
  });
}
